import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Input,
  Label,
} from "reactstrap";

import { useFetchLocationsQuery } from "../api/cmsPanelsApi";
import {
  useCreateReturnMutation,
  useLazySearchReturnSalesQuery,
  useLookupReturnMutation,
} from "../api/returnsApi";
import { getApiErrorMessage } from "../api/errorUtils";
import { printReturnRefundSlip } from "../components/Print/commerceDocumentPrint";
import { useTranslation } from "../hooks/useTranslation";
import {
  buildSaleItemDisplayLabel,
  classifyOptionSegment,
  formatMoney,
  formatSaleDateTime,
  inferReturnableSaleLookupPayload,
  isExactSaleNumberQuery,
  resolvePaymentMethodLabel,
  shouldRunHistoricalSaleSearch,
  useDebounce,
} from "./salesUtils";
import { buildItemOptionChips, resolveItemImage } from "./saleItemPresentation";

const inferSaleLookupPayload = inferReturnableSaleLookupPayload;
const shouldRunProductSaleSearch = shouldRunHistoricalSaleSearch;

const ReturnsPage = () => {
  const t = useTranslation();
  const [searchParams] = useSearchParams();
  const saleLookupInputRef = useRef(null);
  const itemScanInputRef = useRef(null);
  const returnLineRefs = useRef({});
  const consumedPrefillRef = useRef(false);
  const [locationId, setLocationId] = useState("");
  const [saleLookupValue, setSaleLookupValue] = useState("");
  const [itemScanValue, setItemScanValue] = useState("");
  const [refundMethod, setRefundMethod] = useState("CASH");
  const [bankName, setBankName] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [lookupResult, setLookupResult] = useState(null);
  const [aggregateSelections, setAggregateSelections] = useState({});
  const [serializedSelections, setSerializedSelections] = useState({});
  const [completedReturn, setCompletedReturn] = useState(null);
  const [highlightedSaleLineId, setHighlightedSaleLineId] = useState(null);
  const [saleSearchResults, setSaleSearchResults] = useState([]);
  const [saleSearchTotal, setSaleSearchTotal] = useState(0);
  const [saleSearchQuery, setSaleSearchQuery] = useState("");
  // An exact sale number whose items were all returned already: shown as a
  // disabled row so staff see why that specific sale can't be used.
  const [exhaustedExactSale, setExhaustedExactSale] = useState(null);
  const saleSearchRequestIdRef = useRef(0);

  const { data: locations = [], isFetching: isFetchingLocations } =
    useFetchLocationsQuery();
  const [lookupReturn, { isLoading: isLookingUp }] = useLookupReturnMutation();
  const [createReturn, { isLoading: isCreatingReturn }] = useCreateReturnMutation();
  const [searchReturnSales, { isFetching: isSearchingSales }] =
    useLazySearchReturnSalesQuery();
  const prefilledSaleNumber = String(searchParams.get("saleNumber") || "").trim();
  const debouncedSaleLookupValue = useDebounce(saleLookupValue, 350);

  const refundOptions = useMemo(
    () => [
      { value: "CASH", label: t("commerceCommon.paymentCash"), icon: "icon-money-coins" },
      { value: "CARD", label: t("commerceCommon.paymentCard"), icon: "icon-credit-card" },
      { value: "BANK", label: t("commerceCommon.paymentBankTransfer"), icon: "icon-bank" },
    ],
    [t]
  );

  useEffect(() => {
    if (!locationId && locations.length) {
      setLocationId(String(locations[0].id));
    }
  }, [locationId, locations]);

  useEffect(() => {
    if (prefilledSaleNumber && !lookupResult && !completedReturn) {
      setSaleLookupValue(prefilledSaleNumber);
    }
  }, [prefilledSaleNumber, lookupResult, completedReturn]);

  useEffect(() => {
    if (
      !prefilledSaleNumber ||
      consumedPrefillRef.current ||
      !locationId ||
      lookupResult ||
      completedReturn ||
      isLookingUp
    ) {
      return;
    }

    consumedPrefillRef.current = true;

    lookupReturn({ saleNumber: prefilledSaleNumber })
      .unwrap()
      .then((result) => {
        if (result?.status === "ready") {
          setLookupResult(result);
          hydrateSelectionsFromLookup(result);
          setCompletedReturn(null);
          setWorkerFeedback(
            "success",
            t("storeReturn.saleLoadedForReturn").replace(
              "{saleNumber}",
              result.sale?.saleNumber || ""
            )
          );
          setSaleLookupValue("");
          return;
        }

        setLookupResult(null);
        setAggregateSelections({});
        setSerializedSelections({});
        setWorkerFeedback(
          result?.status === "not_found" ? "danger" : "warning",
          result?.message || t("storeReturn.nothingReturnable")
        );
      })
      .catch((error) => {
        setLookupResult(null);
        setAggregateSelections({});
        setSerializedSelections({});
        setWorkerFeedback(
          "danger",
          getApiErrorMessage(error, t("storeReturn.lookupLoadError"), {
            404: t("storeReturn.saleNotFound"),
          })
        );
      });
  }, [
    completedReturn,
    isLookingUp,
    locationId,
    lookupResult,
    lookupReturn,
    prefilledSaleNumber,
    t,
  ]);

  useEffect(() => {
    if (completedReturn) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      if (lookupResult) {
        itemScanInputRef.current?.focus();
        itemScanInputRef.current?.select?.();
        return;
      }

      saleLookupInputRef.current?.focus();
      saleLookupInputRef.current?.select?.();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [completedReturn, lookupResult, locationId]);

  useEffect(() => {
    if (!highlightedSaleLineId) {
      return undefined;
    }

    const node = returnLineRefs.current[highlightedSaleLineId];
    if (node?.scrollIntoView) {
      node.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }

    const timeout = window.setTimeout(() => setHighlightedSaleLineId(null), 1800);
    return () => window.clearTimeout(timeout);
  }, [highlightedSaleLineId]);

  useEffect(() => {
    if (lookupResult || completedReturn) {
      setSaleSearchResults([]);
      setSaleSearchTotal(0);
      setSaleSearchQuery("");
      return undefined;
    }

    const query = String(debouncedSaleLookupValue || "").trim();
    if (!shouldRunProductSaleSearch(query)) {
      setSaleSearchResults([]);
      setSaleSearchTotal(0);
      setSaleSearchQuery("");
      return undefined;
    }

    const requestId = saleSearchRequestIdRef.current + 1;
    saleSearchRequestIdRef.current = requestId;

    searchReturnSales({
      q: query,
      page: 1,
      pageSize: 20,
    })
      .unwrap()
      .then((result) => {
        if (saleSearchRequestIdRef.current !== requestId) {
          return;
        }

        setSaleSearchQuery(result?.query || query);
        setSaleSearchResults(Array.isArray(result?.items) ? result.items : []);
        setSaleSearchTotal(Number(result?.total || 0));
      })
      .catch(() => {
        if (saleSearchRequestIdRef.current !== requestId) {
          return;
        }

        setSaleSearchResults([]);
        setSaleSearchTotal(0);
        setSaleSearchQuery(query);
      });

    return undefined;
  }, [
    completedReturn,
    debouncedSaleLookupValue,
    lookupResult,
    searchReturnSales,
  ]);

  const selectedReturnLines = useMemo(() => {
    if (!lookupResult?.lines?.length) {
      return [];
    }

    return lookupResult.lines
      .map((line) => {
        if (line.inventoryMode === "aggregate") {
          const quantity = Number(aggregateSelections[line.saleLineId] || 0);
          if (quantity <= 0) {
            return null;
          }

          return {
            ...line,
            returnedQuantity: quantity,
            refundAmount: Number(
              (
                Number((line.finalUnitPriceAmount ?? line.unitPriceAmount) || 0) * quantity
              ).toFixed(2)
            ),
            selectedUnits: [],
          };
        }

        const unitIds = serializedSelections[line.saleLineId] || [];
        if (!unitIds.length) {
          return null;
        }

        const selectedUnits = (line.units || []).filter((unit) =>
          unitIds.includes(unit.saleLineUnitId)
        );

        return {
          ...line,
          returnedQuantity: unitIds.length,
          refundAmount: Number(
            (
              Number((line.finalUnitPriceAmount ?? line.unitPriceAmount) || 0) *
              unitIds.length
            ).toFixed(2)
          ),
          selectedUnits: selectedUnits.length
            ? selectedUnits
            : unitIds.map((saleLineUnitId) => ({ saleLineUnitId })),
        };
      })
      .filter(Boolean);
  }, [aggregateSelections, lookupResult, serializedSelections]);

  const totalRefundAmount = useMemo(
    () =>
      selectedReturnLines.reduce(
        (total, line) => total + Number(line.refundAmount || 0),
        0
      ),
    [selectedReturnLines]
  );

  const selectedItemCount = useMemo(
    () =>
      selectedReturnLines.reduce(
        (total, line) => total + Number(line.returnedQuantity || 0),
        0
      ),
    [selectedReturnLines]
  );

  const setWorkerFeedback = (type, message) => {
    setFeedback({
      type,
      message,
      id: `${Date.now()}-${Math.random()}`,
    });
  };

  const handleSaleLookupChange = (value) => {
    setSaleLookupValue(value);
    setExhaustedExactSale(null);
  };

  const resetSaleLookupField = () => {
    setSaleLookupValue("");
    setSaleSearchResults([]);
    setSaleSearchTotal(0);
    setSaleSearchQuery("");
    window.requestAnimationFrame(() => {
      saleLookupInputRef.current?.focus();
      saleLookupInputRef.current?.select?.();
    });
  };

  const clearSaleSearchResults = () => {
    setSaleSearchResults([]);
    setSaleSearchTotal(0);
    setSaleSearchQuery("");
  };

  const resetItemScanField = () => {
    setItemScanValue("");
    window.requestAnimationFrame(() => {
      itemScanInputRef.current?.focus();
      itemScanInputRef.current?.select?.();
    });
  };

  const hydrateSelectionsFromLookup = (result) => {
    const nextAggregateSelections = {};
    const nextSerializedSelections = {};

    for (const line of result?.lines || []) {
      if (line.inventoryMode === "aggregate") {
        nextAggregateSelections[line.saleLineId] = 0;
        continue;
      }

      const selectedUnits = (line.units || [])
        .filter((unit) => unit.selected)
        .map((unit) => unit.saleLineUnitId);

      if (selectedUnits.length) {
        nextSerializedSelections[line.saleLineId] = selectedUnits;
      }
    }

    setAggregateSelections(nextAggregateSelections);
    setSerializedSelections(nextSerializedSelections);
  };

  const clearSaleContext = () => {
    setExhaustedExactSale(null);
    setLookupResult(null);
    setAggregateSelections({});
    setSerializedSelections({});
    setHighlightedSaleLineId(null);
    setFeedback(null);
    resetSaleLookupField();
  };

  const applyBarcodeMatchResult = (result) => {
    const matchedLine = result?.lines?.[0];
    if (!matchedLine) {
      return;
    }

    if (result.matchType === "serialized_unit" || matchedLine.inventoryMode === "serialized") {
      const unit = matchedLine.units?.[0];
      if (!unit?.saleLineUnitId) {
        setWorkerFeedback("warning", t("storeReturn.barcodeMatchFailed"));
        return;
      }

      setSerializedSelections((current) => {
        const existing = current[matchedLine.saleLineId] || [];
        if (existing.includes(unit.saleLineUnitId)) {
          setWorkerFeedback("warning", t("storeReturn.itemAlreadySelected"));
          return current;
        }

        return {
          ...current,
          [matchedLine.saleLineId]: [...existing, unit.saleLineUnitId],
        };
      });

      setLookupResult((current) => {
        if (!current?.lines?.length) {
          return current;
        }

        return {
          ...current,
          lines: current.lines.map((line) => {
            if (line.saleLineId !== matchedLine.saleLineId) {
              return line;
            }

            const existingUnits = line.units || [];
            const hasUnit = existingUnits.some(
              (entry) => entry.saleLineUnitId === unit.saleLineUnitId
            );

            return {
              ...line,
              requiresSoldUnitScan: false,
              units: hasUnit ? existingUnits : [...existingUnits, unit],
            };
          }),
        };
      });
    } else {
      setAggregateSelections((current) => {
        const currentQty = Number(current[matchedLine.saleLineId] || 0);
        if (currentQty >= Number(matchedLine.returnableQuantity || 0)) {
          setWorkerFeedback("warning", t("storeReturn.noReturnableQuantityLeft"));
          return current;
        }

        return {
          ...current,
          [matchedLine.saleLineId]: currentQty + 1,
        };
      });
    }

    setHighlightedSaleLineId(matchedLine.saleLineId);
    setWorkerFeedback(
      "success",
      t("storeReturn.itemMatchedForReturn").replace(
        "{name}",
        matchedLine.productTitle || matchedLine.variantTitle || t("commerceCommon.itemFallback")
      )
    );
  };

  const applyLookupReadyResult = (result) => {
    setExhaustedExactSale(null);
    setLookupResult(result);
    hydrateSelectionsFromLookup(result);
    setCompletedReturn(null);
    clearSaleSearchResults();

    const matchedLine = result.lines?.find(
      (line) => line.saleLineId === result.matchedSaleLineId
    );
    if (matchedLine && result.matchType === "aggregate_line") {
      applyBarcodeMatchResult({
        ...result,
        lines: [matchedLine],
        matchType: "aggregate_line",
      });
      return;
    }

    setWorkerFeedback(
      "success",
      t("storeReturn.saleLoadedForReturn").replace(
        "{saleNumber}",
        result.sale?.saleNumber || ""
      )
    );
  };

  const loadSaleForReturn = async (saleNumber, { clearInput = true } = {}) => {
    const normalizedSaleNumber = String(saleNumber || "").trim().toUpperCase();
    if (!normalizedSaleNumber) {
      return;
    }

    try {
      const result = await lookupReturn({ saleNumber: normalizedSaleNumber }).unwrap();
      if (result?.status === "ready") {
        applyLookupReadyResult(result);
        if (clearInput) {
          setSaleLookupValue("");
        }
        return;
      }

      setLookupResult(null);
      setAggregateSelections({});
      setSerializedSelections({});
      setWorkerFeedback(
        result?.status === "not_found" ? "danger" : "warning",
        result?.message || t("storeReturn.nothingReturnable")
      );
    } catch (error) {
      setLookupResult(null);
      setAggregateSelections({});
      setSerializedSelections({});
      setWorkerFeedback(
        "danger",
        getApiErrorMessage(error, t("storeReturn.lookupLoadError"), {
          404: t("storeReturn.saleNotFound"),
        })
      );
    }
  };

  const handleSaleLookupSubmit = async (event) => {
    event.preventDefault();

    const query = String(saleLookupValue || "").trim();
    setExhaustedExactSale(null);
    if (!query) {
      resetSaleLookupField();
      return;
    }

    // Exact sale number / barcode keep the fast existing lookup path.
    if (isExactSaleNumberQuery(query) || !shouldRunProductSaleSearch(query)) {
      const payload = inferSaleLookupPayload(query);
      if (!payload) {
        resetSaleLookupField();
        return;
      }

      try {
        const result = await lookupReturn(payload).unwrap();
        if (result?.status === "ready") {
          applyLookupReadyResult(result);
        } else if (result?.status === "not_returnable" && result?.sale) {
          // The exact sale exists but nothing is left to return: identify it as a
          // disabled row instead of a toast that disappears.
          setLookupResult(null);
          setAggregateSelections({});
          setSerializedSelections({});
          setExhaustedExactSale({ sale: result.sale, saleLines: result.saleLines || [] });
        } else {
          setLookupResult(null);
          setAggregateSelections({});
          setSerializedSelections({});
          setWorkerFeedback(
            result?.status === "not_found" ? "danger" : "warning",
            result?.message || t("storeReturn.nothingReturnable")
          );
        }
      } catch (error) {
        setLookupResult(null);
        setAggregateSelections({});
        setSerializedSelections({});
        setWorkerFeedback(
          "danger",
          getApiErrorMessage(error, t("storeReturn.lookupLoadError"), {
            404: t("storeReturn.saleNotFound"),
          })
        );
      } finally {
        resetSaleLookupField();
      }
      return;
    }

    // Broader text query: try barcode/SKU exact lookup first, then fall back to search results.
    try {
      const result = await lookupReturn({ inventoryUnitBarcode: query }).unwrap();
      if (result?.status === "ready") {
        applyLookupReadyResult(result);
        setSaleLookupValue("");
        return;
      }
    } catch (_error) {
      // Fall through to historical sale search results.
    }

    if (saleSearchResults.length === 1) {
      await loadSaleForReturn(saleSearchResults[0]?.sale?.saleNumber);
      setSaleLookupValue("");
      return;
    }

    if (saleSearchResults.length > 1) {
      setWorkerFeedback("warning", t("storeReturn.chooseSaleFromSearch"));
      return;
    }

    // Kick a fresh search if debounce has not populated results yet.
    try {
      const searchResult = await searchReturnSales({
        q: query,
        page: 1,
        pageSize: 20,
      }).unwrap();
      const items = Array.isArray(searchResult?.items) ? searchResult.items : [];
      setSaleSearchQuery(searchResult?.query || query);
      setSaleSearchResults(items);
      setSaleSearchTotal(Number(searchResult?.total || 0));

      if (items.length === 1) {
        await loadSaleForReturn(items[0]?.sale?.saleNumber);
        setSaleLookupValue("");
        return;
      }

      if (items.length > 1) {
        setWorkerFeedback("warning", t("storeReturn.chooseSaleFromSearch"));
        return;
      }

      setWorkerFeedback("warning", t("storeReturn.noMatchingSales"));
    } catch (error) {
      setWorkerFeedback(
        "danger",
        getApiErrorMessage(error, t("storeReturn.saleSearchError"))
      );
    }
  };

  const handleSelectSaleSearchResult = async (saleNumber) => {
    await loadSaleForReturn(saleNumber);
  };

  const handleItemScanSubmit = async (event) => {
    event.preventDefault();

    const barcode = String(itemScanValue || "").trim();
    if (!barcode) {
      resetItemScanField();
      return;
    }

    if (!lookupResult?.sale?.id) {
      setWorkerFeedback("warning", t("storeReturn.findSaleBeforeReturn"));
      resetItemScanField();
      return;
    }

    try {
      const result = await lookupReturn({
        originalSaleId: lookupResult.sale.id,
        inventoryUnitBarcode: barcode,
      }).unwrap();

      if (result?.status === "ready") {
        applyBarcodeMatchResult(result);
      } else {
        setWorkerFeedback(
          result?.status === "not_found" ? "danger" : "warning",
          result?.message || t("storeReturn.barcodeNotReturnable")
        );
      }
    } catch (error) {
      setWorkerFeedback(
        "danger",
        getApiErrorMessage(error, t("storeReturn.barcodeLookupError"), {
          404: t("storeReturn.saleNotFound"),
        })
      );
    } finally {
      resetItemScanField();
    }
  };

  const adjustAggregateQuantity = (saleLineId, delta, max) => {
    setAggregateSelections((current) => {
      const currentValue = Number(current[saleLineId] || 0);
      const nextValue = Math.max(0, Math.min(max, currentValue + delta));
      return {
        ...current,
        [saleLineId]: nextValue,
      };
    });
  };

  const clearLineSelection = (line) => {
    if (line.inventoryMode === "aggregate") {
      setAggregateSelections((current) => ({
        ...current,
        [line.saleLineId]: 0,
      }));
      return;
    }

    setSerializedSelections((current) => ({
      ...current,
      [line.saleLineId]: [],
    }));
  };

  const handleCompleteReturn = async () => {
    if (!locationId) {
      setWorkerFeedback("warning", t("storeReturn.chooseLocationBeforeReturn"));
      return;
    }

    if (!lookupResult?.sale?.id) {
      setWorkerFeedback("warning", t("storeReturn.findSaleBeforeReturn"));
      return;
    }

    if (!selectedReturnLines.length) {
      setWorkerFeedback("warning", t("storeReturn.chooseReturnableFirst"));
      return;
    }

    try {
      const result = await createReturn({
        originalSaleId: lookupResult.sale.id,
        locationId: Number(locationId),
        refundMethod,
        bankName:
          refundMethod === "CASH" ? undefined : String(bankName || "").trim() || undefined,
        lines: selectedReturnLines.map((line) => ({
          saleLineId: line.saleLineId,
          quantity: line.inventoryMode === "aggregate" ? line.returnedQuantity : undefined,
          saleLineUnitIds:
            line.inventoryMode === "serialized"
              ? line.selectedUnits.map((unit) => unit.saleLineUnitId)
              : undefined,
        })),
      }).unwrap();

      setCompletedReturn(result);
      setLookupResult(null);
      setAggregateSelections({});
      setSerializedSelections({});
      setBankName("");
      setFeedback(null);
    } catch (error) {
      setWorkerFeedback(
        "danger",
        getApiErrorMessage(error, t("storeReturn.completeReturnError"), {
          404: t("storeReturn.saleNotFoundOnComplete"),
          409: t("storeReturn.itemsNoLongerReturnable"),
        })
      );
    }
  };

  const startNewReturn = () => {
    setCompletedReturn(null);
    setLookupResult(null);
    setAggregateSelections({});
    setSerializedSelections({});
    setRefundMethod("CASH");
    setBankName("");
    setFeedback(null);
    clearSaleSearchResults();
    resetSaleLookupField();
  };

  // Same option-dimension normalization already established on the Sales /
  // Recent Sales pages, reused via the shared salesUtils classifier.
  const buildOptionChips = (optionSummary) =>
    String(optionSummary || "")
      .split(" / ")
      .map((segment) => classifyOptionSegment(segment))
      .filter(Boolean)
      .map((option) => {
        if (option.kind === "color") {
          return t("commerceCommon.optionColorLabel").replace("{value}", option.value);
        }
        if (option.kind === "size") {
          return t("commerceCommon.optionSizeLabel").replace("{value}", option.value);
        }
        return option.name ? `${option.name}: ${option.value}` : option.value;
      });

  const buildItemTitle = (line) =>
    line.productTitle ||
    buildSaleItemDisplayLabel({
      productTitle: line.productTitle,
      variantTitle: line.variantTitle,
      optionSummary: line.optionSummary,
    });

  const renderFeedback = () => {
    if (!feedback) {
      return null;
    }

    const isSuccess = feedback.type === "success";

    return (
      <div
        className={`rt-feedback rt-feedback--${feedback.type}`}
        key={feedback.id}
        role={isSuccess ? "status" : "alert"}
      >
        <i
          className={`tim-icons ${
            isSuccess ? "icon-check-2" : "icon-alert-circle-exc"
          } rt-feedback__icon`}
          aria-hidden="true"
        />
        <p className="rt-feedback__text">{feedback.message}</p>
      </div>
    );
  };

  // Purely presentational: derived from existing lookupResult/completedReturn
  // state, no new business logic or state variables.
  const renderStepIndicator = () => {
    const step1Done = Boolean(lookupResult || completedReturn);
    const step2Active = Boolean(lookupResult && !completedReturn);
    const step2Done = Boolean(completedReturn);
    const step3Active = Boolean(completedReturn);

    const steps = [
      { label: t("storeReturn.stepFindSale"), done: step1Done, active: !step1Done },
      { label: t("storeReturn.stepSelectItems"), done: step2Done, active: step2Active },
      { label: t("storeReturn.stepRefund"), done: false, active: step3Active },
    ];

    return (
      <ol className="rt-steps" aria-label={t("storeReturn.subtitle")}>
        {steps.map((step, index) => (
          <li
            key={step.label}
            className={`rt-step${step.done ? " is-done" : ""}${
              step.active ? " is-active" : ""
            }`}
          >
            <span className="rt-step__marker" aria-hidden="true">
              {step.done ? <i className="tim-icons icon-check-2" /> : index + 1}
            </span>
            <span className="rt-step__label">{step.label}</span>
          </li>
        ))}
      </ol>
    );
  };

  const renderCheckoutPanel = (sticky = true) => (
    <aside
      className={`return-page-checkout${sticky ? " return-page-checkout--sticky" : ""}`}
      aria-label={t("storeReturn.refundTotal")}
    >
      <div className="return-page-checkout__inner rt-checkout">
        <h2 className="rt-checkout__title">{t("storeReturn.refundSummaryTitle")}</h2>

        <div className="rt-checkout__count">
          <i className="tim-icons icon-basket-simple" aria-hidden="true" />
          {t("commerceCommon.itemCount").replace("{count}", selectedItemCount)}
        </div>

        <div className="rt-checkout__total">
          <span className="rt-checkout__total-label">{t("storeReturn.refundTotal")}</span>
          <span className="rt-checkout__total-value">{formatMoney(totalRefundAmount)}</span>
        </div>

        <div className="rt-checkout__payment">
          <span className="sp-label">{t("storeReturn.refundMethodLabel")}</span>
          <div
            className="rt-payment-options"
            role="radiogroup"
            aria-label={t("storeReturn.refundMethodLabel")}
          >
            {refundOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={refundMethod === option.value}
                className={`rt-payment-option${
                  refundMethod === option.value ? " is-active" : ""
                }`}
                onClick={() => setRefundMethod(option.value)}
                disabled={isCreatingReturn || Boolean(completedReturn)}
              >
                <i className={`tim-icons ${option.icon}`} aria-hidden="true" />
                <span>{option.label}</span>
              </button>
            ))}
          </div>
          {refundMethod !== "CASH" && (
            <FormGroup className="rt-bank-field">
              <Label for="returns-bank-name">{t("commerceCommon.bankProvider")}</Label>
              <Input
                id="returns-bank-name"
                type="text"
                placeholder={t("commerceCommon.optional")}
                value={bankName}
                onChange={(event) => setBankName(event.target.value)}
                disabled={isCreatingReturn || Boolean(completedReturn)}
              />
            </FormGroup>
          )}
        </div>

        <Button
          color="success"
          className="rt-complete-btn"
          onClick={handleCompleteReturn}
          disabled={!selectedReturnLines.length || isCreatingReturn || Boolean(completedReturn)}
        >
          {isCreatingReturn ? t("commerceCommon.completing") : t("storeReturn.completeReturn")}
        </Button>
      </div>
    </aside>
  );

  const renderReturnableRow = (line) => {
    const isAggregate = line.inventoryMode === "aggregate";
    const selectedQuantity = isAggregate
      ? Number(aggregateSelections[line.saleLineId] || 0)
      : (serializedSelections[line.saleLineId] || []).length;
    const chips = buildItemOptionChips(line.optionSummary, t);
    const refundAmount =
      Number((line.finalUnitPriceAmount ?? line.unitPriceAmount) || 0) * selectedQuantity;

    return (
      <div
        key={line.saleLineId}
        ref={(node) => {
          returnLineRefs.current[line.saleLineId] = node;
        }}
        className={`rt-item-row${
          highlightedSaleLineId === line.saleLineId ? " is-highlighted" : ""
        }${selectedQuantity > 0 ? " is-selected" : ""}`}
      >
        <div className="rt-item-row__main has-media">
          {renderItemThumb(line)}
          <span className="rt-item-title">{buildItemTitle(line)}</span>
          {line.sku && <span className="rt-item-sku sp-truncate">{line.sku}</span>}
          {chips.length > 0 && (
            <div className="rt-item-chips">
              {chips.map((chip) => (
                <span className="rt-item-chip" key={chip}>
                  {chip}
                </span>
              ))}
            </div>
          )}
          <span className="rt-item-can-return">
            {t("storeReturn.canReturnCount").replace("{count}", line.returnableQuantity)}
          </span>
          <span className="rt-item-meta">
            {t("storeReturn.soldQty")}: {line.soldQuantity} · {t("storeReturn.returnedQty")}:{" "}
            {line.alreadyReturnedQuantity || 0}
          </span>
        </div>

        <div className="rt-item-row__control">
          {isAggregate ? (
            <div className="rt-qty-stepper">
              <button
                type="button"
                className="rt-qty-btn"
                onClick={() => adjustAggregateQuantity(line.saleLineId, -1, line.returnableQuantity)}
                disabled={isCreatingReturn || selectedQuantity <= 0}
                aria-label={t("storeSales.decreaseQty")}
              >
                <span aria-hidden="true">−</span>
              </button>
              <span className="rt-qty-value">{selectedQuantity}</span>
              <button
                type="button"
                className="rt-qty-btn"
                onClick={() => adjustAggregateQuantity(line.saleLineId, 1, line.returnableQuantity)}
                disabled={isCreatingReturn || selectedQuantity >= line.returnableQuantity}
                aria-label={t("storeSales.increaseQty")}
              >
                <span aria-hidden="true">+</span>
              </button>
            </div>
          ) : (
            <span className="rt-scan-hint">
              {line.requiresSoldUnitScan
                ? t("commerceCommon.scanSoldLabelHint")
                : t("commerceCommon.selectedCount").replace("{count}", selectedQuantity)}
            </span>
          )}
        </div>

        <span className="rt-item-row__refund">{formatMoney(refundAmount)}</span>

        <Button
          color="secondary"
          size="sm"
          className="rt-icon-btn"
          onClick={() => clearLineSelection(line)}
          disabled={isCreatingReturn || selectedQuantity <= 0}
          aria-label={`${t("storeReturn.clearLineSelection")} — ${buildItemTitle(line)}`}
          title={t("storeReturn.clearLineSelection")}
        >
          <i className="tim-icons icon-simple-remove" aria-hidden="true" />
        </Button>
      </div>
    );
  };

  // Real catalog image (or a neutral tile) - never a made-up picture.
  const renderItemThumb = (line) => {
    const image = resolveItemImage(line);

    return (
      <span className="sop-thumb" aria-hidden="true">
        {image ? <img src={image} alt="" loading="lazy" /> : null}
      </span>
    );
  };

  const renderExhaustedExactSale = () => {
    const { sale, saleLines } = exhaustedExactSale;
    const line = saleLines[0];
    const chips = line ? buildItemOptionChips(line.optionSummary, t) : [];

    return (
      <div className="rt-search-results" aria-live="polite">
        <div className="rt-result-row is-disabled is-informational" aria-disabled="true">
          {line ? renderItemThumb(line) : null}
          <div className="rt-result-row__main">
            <div className="rt-result-row__top">
              <span className="rt-result-row__number">{sale.saleNumber}</span>
              <span className="rt-result-row__datetime">
                {formatSaleDateTime(sale.completedAt)}
              </span>
            </div>
            {line && <span className="rt-item-title">{buildItemTitle(line)}</span>}
            {chips.length > 0 && (
              <div className="rt-item-chips">
                {chips.map((chip) => (
                  <span className="rt-item-chip" key={chip}>
                    {chip}
                  </span>
                ))}
              </div>
            )}
            {sale.location?.name && (
              <span className="rt-result-row__location">{sale.location.name}</span>
            )}
          </div>
          <div className="rt-result-row__side">
            <span className="rt-result-row__total">{formatMoney(sale.totalAmount)}</span>
            <span className="rt-result-row__exhausted">{t("storeReturn.saleFullyReturned")}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderSaleSearchResult = (item) => {
    const matchedLine = item.matchedLines?.[0];
    const title = matchedLine ? buildItemTitle(matchedLine) : null;
    const chips = matchedLine ? buildItemOptionChips(matchedLine.optionSummary, t) : [];
    const isExhausted = item.hasReturnableMatchedLines === false;

    return (
      <button
        key={item.sale?.id || item.sale?.saleNumber}
        type="button"
        className={`rt-result-row${isExhausted ? " is-disabled" : ""}`}
        onClick={() => handleSelectSaleSearchResult(item.sale?.saleNumber)}
        disabled={isLookingUp || isCreatingReturn || isExhausted}
      >
        {matchedLine ? renderItemThumb(matchedLine) : null}
        <div className="rt-result-row__main">
          <div className="rt-result-row__top">
            <span className="rt-result-row__number">{item.sale?.saleNumber}</span>
            <span className="rt-result-row__datetime">
              {formatSaleDateTime(item.sale?.completedAt)}
            </span>
          </div>
          {title && (
            <>
              <span className="rt-item-title">{title}</span>
              {chips.length > 0 && (
                <div className="rt-item-chips">
                  {chips.map((chip) => (
                    <span className="rt-item-chip" key={chip}>
                      {chip}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
          {item.sale?.location?.name && (
            <span className="rt-result-row__location">{item.sale.location.name}</span>
          )}
        </div>

        <div className="rt-result-row__side">
          <span className="rt-result-row__total">
            {formatMoney(matchedLine?.lineTotalAmount ?? item.sale?.totalAmount)}
          </span>
          {isExhausted ? (
            <span className="rt-result-row__exhausted">
              {t("storeReturn.saleFullyReturned")}
            </span>
          ) : (
            matchedLine && (
              <span className="rt-result-row__returnable">
                {t("storeReturn.returnableUnitsShort").replace(
                  "{count}",
                  matchedLine.returnableQuantity
                )}
              </span>
            )
          )}
        </div>

        {/* No "open" affordance on a row that can't be opened. */}
        {!isExhausted && (
          <i className="tim-icons icon-minimal-right rt-result-row__chevron" aria-hidden="true" />
        )}
      </button>
    );
  };

  return (
    <div className="content sales-premium sales-page return-page">
      <Card className="sales-page-card">
        <CardHeader className="sales-page-header">
          <div>
            <div className="sp-title">
              {completedReturn ? t("storeReturn.titleCompleted") : t("storeReturn.title")}
            </div>
            <p className="sp-subtitle">
              {completedReturn
                ? t("storeReturn.returnSaved").replace(
                    "{returnNumber}",
                    completedReturn.returnNumber
                  )
                : t("storeReturn.subtitle")}
            </p>
          </div>
          <div className="sales-page-header__actions">
            {completedReturn ? null : lookupResult ? (
              <Button
                color="secondary"
                size="sm"
                onClick={clearSaleContext}
                disabled={isCreatingReturn}
              >
                {t("commerceCommon.clearLookup")}
              </Button>
            ) : null}
          </div>
        </CardHeader>

        <CardBody className="sales-page-body">
          {completedReturn ? (
            <div className="rt-success">
              <div className="rt-success__badge" aria-hidden="true">
                <i className="tim-icons icon-check-2" />
              </div>
              <h2 className="rt-success__title">{t("storeReturn.returnCompletedTitle")}</h2>
              <p className="rt-success__subtitle">
                {t("storeReturn.returnSaved").replace(
                  "{returnNumber}",
                  completedReturn.returnNumber
                )}
              </p>

              <div className="rt-success__facts">
                <div className="rt-fact">
                  <span className="rt-fact__icon">
                    <i className="tim-icons icon-notes" aria-hidden="true" />
                  </span>
                  <span className="rt-fact__body">
                    <span className="rt-fact__label">{t("commerceCommon.returnNumber")}</span>
                    <span className="rt-fact__value">{completedReturn.returnNumber}</span>
                  </span>
                </div>
                {completedReturn.originalSale?.saleNumber && (
                  <div className="rt-fact">
                    <span className="rt-fact__icon">
                      <i className="tim-icons icon-notes" aria-hidden="true" />
                    </span>
                    <span className="rt-fact__body">
                      <span className="rt-fact__label">{t("commerceCommon.originalSale")}</span>
                      <span className="rt-fact__value">
                        {completedReturn.originalSale.saleNumber}
                      </span>
                    </span>
                  </div>
                )}
                {completedReturn.location?.name && (
                  <div className="rt-fact">
                    <span className="rt-fact__icon">
                      <i className="tim-icons icon-square-pin" aria-hidden="true" />
                    </span>
                    <span className="rt-fact__body">
                      <span className="rt-fact__label">{t("commerceCommon.location")}</span>
                      <span className="rt-fact__value">{completedReturn.location.name}</span>
                    </span>
                  </div>
                )}
                <div className="rt-fact">
                  <span className="rt-fact__icon">
                    <i className="tim-icons icon-credit-card" aria-hidden="true" />
                  </span>
                  <span className="rt-fact__body">
                    <span className="rt-fact__label">
                      {t("storeReturn.refundMethodLabel")}
                    </span>
                    <span className="rt-fact__value">
                      {resolvePaymentMethodLabel(completedReturn.refundMethod, t)}
                    </span>
                  </span>
                </div>
                {completedReturn.completedAt && (
                  <div className="rt-fact">
                    <span className="rt-fact__icon">
                      <i className="tim-icons icon-calendar-60" aria-hidden="true" />
                    </span>
                    <span className="rt-fact__body">
                      <span className="rt-fact__label">{t("storeSales.dateAndTime")}</span>
                      <span className="rt-fact__value">
                        {formatSaleDateTime(completedReturn.completedAt)}
                      </span>
                    </span>
                  </div>
                )}
              </div>

              <div className="rt-success__total">
                <span className="rt-success__total-label">{t("storeReturn.refundTotal")}</span>
                <span className="rt-success__total-value">
                  {formatMoney(completedReturn.totalRefundAmount)}
                </span>
              </div>

              <h3 className="rt-items-title">{t("storeReturn.returnedItemsTitle")}</h3>
              <div className="rt-receipt-table">
                <div className="rt-receipt-row rt-receipt-row--header" aria-hidden="true">
                  <span>#</span>
                  <span>{t("commerceCommon.itemHeader")}</span>
                  <span>{t("commerceCommon.qty")}</span>
                  <span>{t("commerceCommon.price")}</span>
                  <span>{t("commerceCommon.refund")}</span>
                </div>
                {(completedReturn.lines || []).map((line, index) => {
                  const lineChips = buildOptionChips(line.optionSummary);
                  return (
                    <div className="rt-receipt-row" key={line.id}>
                      <span className="rt-receipt-row__index">{index + 1}</span>
                      <div className="rt-receipt-row__item">
                        <span className="rt-item-title">{buildItemTitle(line)}</span>
                        {line.sku && (
                          <span className="rt-item-sku sp-truncate">{line.sku}</span>
                        )}
                        {(lineChips.length > 0 || line.brandName) && (
                          <div className="rt-item-chips">
                            {line.brandName && (
                              <span className="rt-item-chip">{line.brandName}</span>
                            )}
                            {lineChips.map((chip) => (
                              <span className="rt-item-chip" key={chip}>
                                {chip}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <span className="rt-receipt-row__qty">{line.returnedQuantity}</span>
                      <span className="rt-receipt-row__price">
                        {formatMoney(line.finalUnitPriceAmount ?? line.unitPriceAmount)}
                      </span>
                      <span className="rt-receipt-row__total">
                        {formatMoney(line.refundAmount)}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="rt-success__actions">
                <Button
                  color="success"
                  className="rt-success-action rt-success-action--primary"
                  onClick={startNewReturn}
                >
                  <i className="tim-icons icon-refresh-01" aria-hidden="true" />
                  <span className="rt-success-action__text">
                    <span className="rt-success-action__title">
                      {t("commerceCommon.newReturn")}
                    </span>
                    <span className="rt-success-action__hint">
                      {t("storeReturn.newReturnHint")}
                    </span>
                  </span>
                </Button>
                <Button
                  color="secondary"
                  className="rt-success-action"
                  onClick={() => printReturnRefundSlip(completedReturn)}
                >
                  <i className="tim-icons icon-paper" aria-hidden="true" />
                  <span className="rt-success-action__text">
                    <span className="rt-success-action__title">
                      {t("commerceCommon.viewPrintRefundSlip")}
                    </span>
                    <span className="rt-success-action__hint">
                      {t("storeReturn.printSlipHint")}
                    </span>
                  </span>
                </Button>
              </div>
            </div>
          ) : (
            <>
              {renderStepIndicator()}
              <div
                className={`return-page-layout${
                  lookupResult ? " return-page-layout--split" : ""
                }`}
              >
                <div className="return-page-main">
                  <section className="rt-search-card">
                    {renderFeedback()}

                    <div className="rt-search-card__top">
                      <FormGroup className="rt-location-field">
                        <Label for="returns-location">{t("storeReturn.returnLocation")}</Label>
                        <Input
                          id="returns-location"
                          type="select"
                          value={locationId}
                          onChange={(event) => setLocationId(event.target.value)}
                          disabled={isFetchingLocations || isCreatingReturn}
                        >
                          <option value="">{t("commerceCommon.chooseLocation")}</option>
                          {locations.map((location) => (
                            <option key={location.id} value={location.id}>
                              {location.name ||
                                t("commerceCommon.locationFallback").replace(
                                  "{id}",
                                  location.id
                                )}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </div>

                    {!lookupResult ? (
                      <Form className="rt-search-form" onSubmit={handleSaleLookupSubmit}>
                        <Label for="returns-sale-lookup-input" className="sr-only">
                          {t("storeReturn.saleLookupLabel")}
                        </Label>
                        <div className="rt-field-wrap">
                          <div className="rt-field-control">
                            <i
                              className="tim-icons icon-zoom-split rt-field-icon"
                              aria-hidden="true"
                            />
                            <Input
                              id="returns-sale-lookup-input"
                              innerRef={saleLookupInputRef}
                              type="text"
                              autoComplete="off"
                              className="rt-field-input"
                              placeholder={t("storeReturn.saleLookupPlaceholder")}
                              value={saleLookupValue}
                              onChange={(event) => handleSaleLookupChange(event.target.value)}
                              disabled={!locationId || isLookingUp || isCreatingReturn}
                              aria-label={t("storeReturn.saleLookupLabel")}
                            />
                          </div>
                          <Button
                            color="primary"
                            type="submit"
                            className="rt-field-submit"
                            disabled={
                              !locationId ||
                              !saleLookupValue.trim() ||
                              isLookingUp ||
                              isSearchingSales ||
                              isCreatingReturn
                            }
                          >
                            {isLookingUp || isSearchingSales
                              ? t("commerceCommon.lookingUp")
                              : t("storeReturn.findSale")}
                          </Button>
                        </div>

                        {!lookupResult && exhaustedExactSale ? renderExhaustedExactSale() : null}

                        {!lookupResult && (saleSearchResults.length > 0 || saleSearchQuery) ? (
                          <div className="rt-search-results" aria-live="polite">
                            <p className="rt-search-results__count">
                              {isSearchingSales
                                ? t("commerceCommon.lookingUp")
                                : t("storeReturn.saleSearchResults")
                                    .replace(
                                      "{count}",
                                      String(saleSearchTotal || saleSearchResults.length)
                                    )
                                    .replace(
                                      "{query}",
                                      saleSearchQuery || saleLookupValue.trim()
                                    )}
                            </p>
                            {saleSearchResults.length ? (
                              <div className="rt-search-results__list sp-scrollbar-hidden">
                                {saleSearchResults.map(renderSaleSearchResult)}
                              </div>
                            ) : (
                              <p className="rt-search-results__empty">
                                {t("storeReturn.noMatchingSales")}
                              </p>
                            )}
                          </div>
                        ) : null}
                      </Form>
                    ) : (
                      <div className="rt-selected-sale">
                        <div className="rt-selected-sale__main">
                          <span className="rt-selected-sale__number">
                            {lookupResult.sale?.saleNumber}
                          </span>
                          {lookupResult.sale?.completedAt && (
                            <span className="rt-selected-sale__datetime">
                              {formatSaleDateTime(lookupResult.sale.completedAt)}
                            </span>
                          )}
                        </div>
                        <div className="rt-selected-sale__facts">
                          {lookupResult.sale?.location?.name && (
                            <span className="rt-selected-sale__fact">
                              <i className="tim-icons icon-square-pin" aria-hidden="true" />
                              {lookupResult.sale.location.name}
                            </span>
                          )}
                          <span className="rt-selected-sale__fact rt-selected-sale__fact--total">
                            {formatMoney(lookupResult.sale?.totalAmount)}
                          </span>
                        </div>
                      </div>
                    )}
                  </section>

                  {lookupResult ? (
                    <>
                      <section className="rt-scan-card">
                        <Form onSubmit={handleItemScanSubmit}>
                          <Label for="returns-item-scan-input" className="sr-only">
                            {t("storeReturn.itemScanLabel")}
                          </Label>
                          <div className="rt-field-wrap">
                            <div className="rt-field-control">
                              <i
                                className="tim-icons icon-tag rt-field-icon rt-field-icon--scan"
                                aria-hidden="true"
                              />
                              <Input
                                id="returns-item-scan-input"
                                innerRef={itemScanInputRef}
                                type="text"
                                autoComplete="off"
                                className="rt-field-input rt-scan-input"
                                placeholder={t("storeReturn.itemScanPlaceholder")}
                                value={itemScanValue}
                                onChange={(event) => setItemScanValue(event.target.value)}
                                disabled={!locationId || isLookingUp || isCreatingReturn}
                                aria-label={t("storeReturn.itemScanLabel")}
                              />
                            </div>
                            <Button
                              color="primary"
                              type="submit"
                              className="rt-field-submit"
                              disabled={
                                !locationId ||
                                !itemScanValue.trim() ||
                                isLookingUp ||
                                isCreatingReturn
                              }
                            >
                              {isLookingUp
                                ? t("commerceCommon.lookingUp")
                                : t("commerceCommon.scan")}
                            </Button>
                          </div>
                          <p className="rt-scan-hint">{t("storeReturn.itemScanLabel")}</p>
                        </Form>
                      </section>

                      <section className="rt-items-card">
                        <div className="rt-items-card__head">
                          <h2 className="rt-items-title">
                            {t("storeReturn.returnableItems")}
                          </h2>
                        </div>
                        <div className="rt-items-table">
                          {(lookupResult.lines || []).map(renderReturnableRow)}
                        </div>
                      </section>

                      <div className="return-page-checkout-mobile">
                        {renderCheckoutPanel(false)}
                      </div>
                    </>
                  ) : (
                    <div className="sp-empty sp-empty--compact">
                      <div className="sp-empty-icon">
                        <i className="tim-icons icon-zoom-split" />
                      </div>
                      <div className="sp-empty-title">{t("storeReturn.readyToLookup")}</div>
                      <p className="sp-empty-desc">{t("storeReturn.aggregateOrScanHint")}</p>
                      <p className="sp-empty-desc">{t("storeReturn.uniqueLabelHint")}</p>
                    </div>
                  )}
                </div>

                {lookupResult ? renderCheckoutPanel(true) : null}
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default ReturnsPage;
